import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import TextInput from "../../Components/InputComponents/TextInput";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";
import { useLocation, useNavigate } from "react-router-dom";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";

const AddStaff = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    name: "",
    passport_no: "",
    profession: "",
    branch: "",
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPass, setConfirmPass] = useState("");

  // State to collect text input data
  const [staffData, setStaffData] = useState({
    ED_Name: null,
    ED_Email: null,
    ED_ContactNumber: null,
    ED_Address: null,
    ED_Department: null,
    ED_Designation: null,
    ED_Dateofbirth: null,
    ED_Dateofjoin: null,
    ED_Branch: null,
    ED_EmploymentStatus: null,
    ED_ReportingPerson: null,
    ED_Salary: null,
    ED_PayFrequency: null,
    ED_BonusInfo: null,
    ED_MaxLeave: null,
    ED_SickLeave: null,
    ED_Remarks: null,
    ED_Referal: null,
    ED_Status: 1,
    ED_UserName: null,
    ED_Password: null,
    ED_Role: null,
  });

  // State for error handling
  const [errors, setErrors] = useState({});

  const [designationList, setDesignationList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [employmentStatus, setEmploymentStatus] = useState([]);
  const [employeeRoleList, setEmployeeRoleList] = useState([]);
  const [reportingPersonList, setReportingPersonList] = useState([]);
  const [salaryPayFreq, setSalaryPayFreq] = useState([]);

  const getDesignation = async () => {
    try {
      const res = await axiosAPI.get(`/Designation/active/list`);
      setDesignationList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/Designation/list`);
        setDesignationList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getDepartment = async () => {
    try {
      const res = await axiosAPI.get(`/Department/active/list`);
      setDepartmentList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/Department/list`);
        setDepartmentList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getBranches = async () => {
    try {
      const res = await axiosAPI.get(`/Branch/active/list`);
      setBranchList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getEmploymentStatus = async () => {
    try {
      const res = await axiosAPI.get(`/EmpStatus/active/list`);
      setEmploymentStatus(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/EmpStatus/list`);
        setEmploymentStatus(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getEmployeeRoleList = async () => {
    try {
      const res = await axiosAPI.get(`/UserRole/list/active`);
      setEmployeeRoleList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getReportingPersonList = async () => {
    try {
      const res = await axiosAPI.get(`/Employee/reportingstaff`);
      setReportingPersonList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getSalaryPayList = async () => {
    try {
      const res = await axiosAPI.get(`/SalaryPayFrequency/list`);
      setSalaryPayFreq(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Validate the form fields
  const validateForm = () => {
    const mandatoryFields = [
      { field: "ED_Name", label: "Full Name" },
      { field: "ED_Dateofbirth", label: "Date of Birth" },
      { field: "ED_ContactNumber", label: "Contact Number", type: "phone" },
      { field: "ED_Email", label: "Email Address", type: "email" },
      { field: "ED_Designation", label: "Designation" },
      { field: "ED_Department", label: "Department" },
      { field: "ED_Branch", label: "Branch" },
      { field: "ED_EmploymentStatus", label: "Employment Status" },
      { field: "ED_UserName", label: "Username" },
      { field: "ED_Password", label: "Password", type: "password" },
      { field: "ED_Role", label: "Role" },
    ];

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{10}$/; // Strictly 10-digit phone number

    // Clear previous errors
    const formErrors = {};

    // Loop through each mandatory field for basic and type-specific validation
    for (let { field, label, type } of mandatoryFields) {
      const value = staffData[field];

      if (!value) {
        formErrors[field] = `${label} is required.`;
      } else {
        if (type === "email" && !emailRegex.test(value)) {
          formErrors[field] = `Please enter a valid ${label}.`;
        }

        if (type === "phone") {
          if (!phoneRegex.test(value)) {
            formErrors[
              field
            ] = `Please enter a valid ${label} (10-digit number).`;
          } else if (value.length < 10) {
            formErrors[field] = `${label} must have at least 10 digits.`;
          }
        }

        if (type === "password" && value.length < 8) {
          formErrors[field] = `${label} must have at least 8 characters.`;
        }
      }
    }
    // Date validation (outside loop)
    if (staffData.ED_Dateofbirth === "Invalid Date") {
      formErrors["ED_Dateofbirth"] = "Date of Birth format is invalid.";
    }
    if (staffData.ED_Dateofjoin === "Invalid Date") {
      formErrors["ED_Dateofjoin"] = "Date of Joining format is invalid.";
    }

    // Confirm password validation (outside loop)
    if (!confirmPass) {
      formErrors["confirmpass"] = "Confirm Password is required.";
    } else if (staffData?.ED_Password !== confirmPass) {
      formErrors["confirmpass"] = "Password mismatch.";
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }

    return true;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrors({
      ...errors,
      [name]: "",
    });

    setStaffData({
      ...staffData,
      [name]: value,
    });
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      axiosAPI
        ?.post("/Employee/add", staffData)
        .then((res) => {
          setIsDisabled(false);
          setLoaderOpen(false);

          if (res?.data?.success) {
            successNotify(res?.data?.message);
            navigate("/Staffs", {
              state: { pagination: paginationState, filterData: filterData },
            });
          } else {
            infoNotify(res?.data?.message);
          }
        })
        .catch((err) => {
          if (!err?.response?.data?.success) {
            let message = err?.response?.data?.message;
            const isDuplicate = message.startsWith("Duplicate");
            if (isDuplicate) {
              infoNotify(
                "This username is already taken. Please choose a different one."
              );
            } else {
              errorNotify(message);
            }
          }

          setIsDisabled(false);
          setLoaderOpen(false);
        });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getDesignation();
    getDepartment();
    getBranches();
    getEmploymentStatus();
    getEmployeeRoleList();
    getReportingPersonList();
    getSalaryPayList();
  }, []);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        margin: "auto",
        position: "relative",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* ============================================================================================================================================================================== */}
      {/* Main Heading */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Add Staff
      </Typography>
      {/* ============================================================================================================================================================================== */}

      {/* Personal Information */}

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Personal Information */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              fontFamily: "Geist, sans-serif",
            }}
          >
            Personal Information
          </Typography>
        </Grid>

        {/*  Full Name */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Full Name <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Full Name"
            placeholder="Enter Full Name"
            name="ED_Name"
            value={staffData?.ED_Name}
            onChange={handleInputChange}
            error={!!errors.ED_Name}
          />
        </Grid>

        {/* Date of Birth */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Date of Birth <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Date of Birth"
            placeholder="Date of Birth"
            name="ED_Dateofbirth"
            value={staffData?.ED_Dateofbirth}
            onChange={handleInputChange}
            error={!!errors?.ED_Dateofbirth}
            type="date"
            dateRestriction="past" // 'none' | 'future' | 'past'
          />
        </Grid>

        {/*  Contact Number */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Contact Number <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            placeholder="Enter Contact Number"
            type="text"
            name="ED_ContactNumber"
            value={staffData?.ED_ContactNumber}
            onChange={handleInputChange}
            error={!!errors?.ED_ContactNumber}
            allowOnlyNumbers={true}
            maxLength={15}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Email <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Email Address"
            placeholder="Enter Email Address"
            type="email"
            name="ED_Email"
            value={staffData?.ED_Email}
            onChange={handleInputChange}
            error={!!errors?.ED_Email}
          />
        </Grid>

        {/* Address */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Address
          </Typography>
          <TextAreaInput
            label="Address"
            placeholder="Enter Address"
            name="ED_Address"
            value={staffData?.ED_Address}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>

      {/* ============================================================================================================================================================================== */}

      {/* Employment Details*/}
      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Employment Details*/}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              fontFamily: "Geist, sans-serif",
              mt: 2,
            }}
          >
            Employment Details
          </Typography>
        </Grid>

        {/* Designation */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Designation <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            placeholder="Choose your Designation"
            name="ED_Designation"
            value={staffData?.ED_Designation}
            onChange={handleInputChange}
            error={!!errors?.ED_Designation}
            options={designationList?.map((item) => ({
              value: item?.DI_Id,
              label: item?.DI_Name,
            }))}
          />
        </Grid>

        {/* Department */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Department <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={staffData.ED_Department}
            name="ED_Department"
            onChange={handleInputChange}
            options={departmentList?.map((item) => ({
              value: item?.DL_Id,
              label: item?.DL_Name,
            }))}
            placeholder="Select Department"
            error={!!errors.ED_Department}
          />
        </Grid>

        {/* Branch */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Branch <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={staffData.ED_Branch}
            name="ED_Branch"
            onChange={handleInputChange}
            options={branchList?.map((item) => ({
              value: item?.BL_Id,
              label: item?.BL_Name,
            }))}
            placeholder="Select Branch"
            error={!!errors.ED_Branch}
          />
        </Grid>

        {/* Supervisor / Manager */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Supervisor / Manager
          </Typography>
          <SelectInput
            value={staffData?.ED_ReportingPerson}
            name="ED_ReportingPerson"
            onChange={handleInputChange}
            options={reportingPersonList?.map((item) => ({
              value: item?.ED_Id,
              label: item?.ED_Name,
            }))}
            placeholder="Select Supervisor / Manager"
          />
        </Grid>

        {/* Employment Status */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Employment Status <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={staffData?.ED_EmploymentStatus}
            name="ED_EmploymentStatus"
            onChange={handleInputChange}
            options={employmentStatus?.map((item) => ({
              value: item?.ES_Id,
              label: item?.ES_Name,
            }))}
            placeholder="Select Employment Status"
            error={!!errors.ED_EmploymentStatus}
          />
        </Grid>

        {/* Date of Joining */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Date of Joining
          </Typography>
          <TextInput
            label="Date of Joining"
            placeholder="Date of Joining"
            name="ED_Dateofjoin"
            value={staffData.ED_Dateofjoin}
            onChange={handleInputChange}
            type="date"
          />
        </Grid>

        {/* Referal */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Referal
          </Typography>
          <TextInput
            label="Referal"
            placeholder="Referal"
            name="ED_Referal"
            value={staffData.ED_Referal}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Internal Notes */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Internal Notes
          </Typography>
          <TextAreaInput
            label="Internal Notes"
            placeholder="Internal Notes"
            name="ED_Remarks"
            value={staffData.ED_Remarks}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>

      {/* ============================================================================================================================================================================== */}
      {/* Salary and Leave Details */}

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Salary and Leave Details */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,

              fontFamily: "Geist, sans-serif",
              mt: 2,
            }}
          >
            Salary and Leave Details
          </Typography>
        </Grid>

        {/* Salary */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Salary
          </Typography>
          <TextInput
            label="Salary"
            placeholder="Salary"
            name="ED_Salary"
            value={staffData.ED_Salary}
            onChange={handleInputChange}
            allowOnlyNumbers={true}
            maxLength={9}
          />
        </Grid>

        {/*  Pay Frequency  */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Pay Frequency
          </Typography>

          <SelectInput
            value={staffData?.ED_PayFrequency}
            name="ED_PayFrequency"
            onChange={handleInputChange}
            options={salaryPayFreq?.map((item) => ({
              value: item?.SPFL_Id,
              label: item?.SPFL_Name,
            }))}
            placeholder="Select Salary Pay Frequency"
          />
        </Grid>

        {/* Maximum Leave (Days)  */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Maximum Leave (Days)
          </Typography>
          <TextInput
            placeholder=" Maximum Leave (Days)"
            type="number"
            name="ED_MaxLeave"
            value={staffData.ED_MaxLeave}
            onChange={handleInputChange}
            allowOnlyNumbers={true}
            maxLength={3}
          />
        </Grid>

        {/* Maximum Sick Leaves (Days) */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Maximum Sick Leaves (Days)
          </Typography>
          <TextInput
            placeholder=" Maximum Sick Leave (Days)"
            type="number"
            name="ED_SickLeave"
            value={staffData.ED_SickLeave}
            onChange={handleInputChange}
            allowOnlyNumbers={true}
            maxLength={3}
          />
        </Grid>

        {/* Bonus Info */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Bonus Info
          </Typography>
          <TextAreaInput
            label="Bonus Info"
            placeholder="Bonus Info"
            name="ED_BonusInfo"
            value={staffData.ED_BonusInfo}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>

      {/* ============================================================================================================================================================================== */}
      {/* Login Credentials */}

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Login Credentials */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,

              fontFamily: "Geist, sans-serif",
              mt: 2,
            }}
          >
            Login Credentials
          </Typography>
        </Grid>

        {/* Username */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Username <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Username"
            placeholder="Username"
            name="ED_UserName"
            value={staffData.ED_UserName}
            onChange={handleInputChange}
            error={!!errors.ED_UserName}
          />
        </Grid>

        {/* Password */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Password <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            type={showPassword ? "text" : "password"}
            endAdornment={
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
            label="Password"
            placeholder="Password"
            name="ED_Password"
            value={staffData.ED_Password}
            onChange={handleInputChange}
            error={!!errors.ED_Password}
          />
        </Grid>

        {/* Confirm Password */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Confirm Password <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            type={showConfirmPassword ? "text" : "password"}
            endAdornment={
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
            label="Confirm Password"
            placeholder="Confirm Password"
            name="confirmpass"
            value={confirmPass}
            onChange={(e) => {
              setConfirmPass(e.target.value);
              setErrors({
                ...errors,
                confirmpass: "",
              });
            }}
            error={!!errors.confirmpass}
          />
        </Grid>

        {/* Role */}
        <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Role <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={staffData?.ED_Role}
            name={`ED_Role`}
            onChange={handleInputChange}
            options={employeeRoleList?.map((item) => ({
              value: item?.RL_Id,
              label: item?.RL_Name,
            }))}
            placeholder="Select Role"
            error={!!errors.ED_Role}
          />
        </Grid>

        {/* Status */}
        {/* <Grid item xs={12} sm={12} md={4} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Status <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={staffData?.ED_Status}
            name={`ED_Status`}
            onChange={handleInputChange}
            options={[
              { value: "1", label: "Active" },
              { value: "2", label: "Inactive" },
            ]}
            placeholder="Select Status"
            error={!!errors.ED_Status}
          />
        </Grid> */}
      </Grid>

      {/* ============================================================================================================================================================================== */}

      {/* Buttons */}
      <Grid
        container
        spacing={2}
        justifyContent={{ xs: "center", sm: "flex-end" }}
        sx={{ mt: 3 }}
      >
        <Grid item>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() =>
              navigate("/Staffs", {
                state: { pagination: paginationState, filterData: filterData },
              })
            }
          >
            Cancel
          </CommonButton>
        </Grid>
        <Grid item>
          <CommonButton
            variant="contained"
            customColor="#007BFF"
            onClick={handleSubmit}
            disabled={isDisabled}
          >
            {isDisabled ? "Saving..." : "Save"}
          </CommonButton>
        </Grid>

        <Backdrop
          open={loaderOpen}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Grid>
    </Paper>
  );
};

export default AddStaff;
